'use client';
import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2, Clock } from 'lucide-react';
import { useT } from '@/lib/useT';
import { useAuthStore } from '@/store/auth.store';
import { api } from '@/lib/api';

type Tab = 'chat' | 'feedback' | 'info';
type InfoMsg = { role: 'user' | 'assistant'; content: string };

// ── Anti-spam (client) ───────────────────────────────────────────────
// Mirrors the server burst throttle (@Throttle burst 3 / 10s on
// /public/widget/*). More than BURST_MAX sends inside BURST_WINDOW, or a
// send faster than MIN_GAP after the previous one, starts a visible
// COOLDOWN countdown during which sending is disabled. The server is the
// hard backstop (bots ignore this); this is the friendly UX layer.
const BURST_MAX = 3;
const BURST_WINDOW = 10_000;
const MIN_GAP = 1_500;
const COOLDOWN = 20_000;

// Localized cooldown notice (kept inline so the widget needs no extra i18n keys).
const COOLDOWN_MSG: Record<string, string> = {
  DE: 'Zu viele Nachrichten — bitte warten Sie',
  EN: 'Too many messages — please wait',
  RU: 'Слишком много сообщений — подождите',
  UA: 'Забагато повідомлень — зачекайте',
  PL: 'Zbyt wiele wiadomości — poczekaj',
  CZ: 'Příliš mnoho zpráv — počkejte',
  MD: 'Prea multe mesaje — așteptați',
  BG: 'Твърде много съобщения — изчакайте',
  KZ: 'Хабарлама тым көп — күтіңіз',
};

const POS_KEY = 'logixa_swidget_bottom';

/**
 * Floating support widget. Three tabs (Info / Feedback / Chat). The closed
 * launcher can be dragged vertically (press-hold → move → release) and its
 * position is remembered. Sends are client-rate-limited with a visible
 * cooldown timer; the API is hard-throttled server-side.
 */
export function SupportChatWidget() {
  const t = useT();
  const user = useAuthStore(s => s.user);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('info');

  // Feedback form state
  const [fbName, setFbName] = useState('');
  const [fbEmail, setFbEmail] = useState('');
  const [fbText, setFbText] = useState('');
  const [fbSending, setFbSending] = useState(false);
  const [fbDone, setFbDone] = useState(false);

  // Info-tab state (Nova chat)
  const [infoMsgs, setInfoMsgs] = useState<InfoMsg[]>([]);
  const [infoInput, setInfoInput] = useState('');
  const [infoLoading, setInfoLoading] = useState(false);
  const infoEndRef = useRef<HTMLDivElement>(null);

  // ── Rate-limit / cooldown ──────────────────────────────────────────
  const sendTimes = useRef<number[]>([]);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  useEffect(() => {
    if (!cooldownUntil) { setCooldownLeft(0); return; }
    const tick = () => {
      const left = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownLeft(left);
      if (left <= 0) setCooldownUntil(0);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  /** Returns true if a send is allowed now; otherwise arms the cooldown. */
  const rateGuard = (): boolean => {
    const now = Date.now();
    if (now < cooldownUntil) return false;
    const recent = sendTimes.current.filter(x => now - x < BURST_WINDOW);
    const last = sendTimes.current[sendTimes.current.length - 1] || 0;
    if (now - last < MIN_GAP || recent.length >= BURST_MAX) {
      sendTimes.current = [...recent, now];
      setCooldownUntil(now + COOLDOWN);
      return false;
    }
    sendTimes.current = [...recent, now];
    return true;
  };

  // Pre-fill name/email when authenticated
  useEffect(() => {
    if (user && !fbName && !fbEmail) {
      setFbName(`${user.name || ''} ${user.surname || ''}`.trim());
      setFbEmail(user.email || '');
    }
  }, [user, fbName, fbEmail]);

  // Auto-scroll Info chat
  useEffect(() => { infoEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [infoMsgs, infoLoading]);

  const lang = (user?.language || (typeof window !== 'undefined' ? localStorage.getItem('logixa_lang') : null) || 'EN').toUpperCase();
  const cooldownNotice = `${COOLDOWN_MSG[lang] || COOLDOWN_MSG.EN} ${cooldownLeft}s`;

  // ── Draggable launcher (vertical) ──────────────────────────────────
  const [btnBottom, setBtnBottom] = useState(24);
  const bottomRef = useRef(24);
  const drag = useRef({ active: false, moved: false, startY: 0, startBottom: 24 });

  useEffect(() => {
    try {
      const v = parseInt(localStorage.getItem(POS_KEY) || '', 10);
      if (!Number.isNaN(v)) { setBtnBottom(v); bottomRef.current = v; }
    } catch { /* private mode */ }
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { active: true, moved: false, startY: e.clientY, startBottom: bottomRef.current };
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d.active) return;
    const dy = d.startY - e.clientY;
    if (Math.abs(dy) > 5) d.moved = true;
    const maxB = (typeof window !== 'undefined' ? window.innerHeight : 800) - 72;
    const nb = Math.max(16, Math.min(maxB, d.startBottom + dy));
    bottomRef.current = nb;
    setBtnBottom(nb);
  };
  const onPointerUp = () => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    if (d.moved) {
      try { localStorage.setItem(POS_KEY, String(Math.round(bottomRef.current))); } catch { /* noop */ }
    } else {
      setOpen(true);
    }
  };

  const submitFeedback = async () => {
    if (fbName.trim().length < 2) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fbEmail)) return;
    if (fbText.trim().length < 5) return;
    if (!rateGuard()) return;
    setFbSending(true);
    try {
      await api.post('/public/widget/feedback', {
        name: fbName.trim(), email: fbEmail.trim(), text: fbText.trim(), lang,
      });
      setFbDone(true);
      setFbText('');
    } catch (e) {
      console.warn('feedback submit', e);
    } finally {
      setFbSending(false);
    }
  };

  const sendInfo = async () => {
    const q = infoInput.trim();
    if (q.length < 2 || infoLoading) return;
    if (!rateGuard()) return;
    const next = [...infoMsgs, { role: 'user' as const, content: q }];
    setInfoMsgs(next);
    setInfoInput('');
    setInfoLoading(true);
    try {
      const r = await api.post<{ answer: string }>('/public/widget/ask', {
        question: q,
        history: next.slice(-6),
        lang,
      });
      setInfoMsgs([...next, { role: 'assistant', content: r.answer }]);
    } catch (e: any) {
      setInfoMsgs([...next, { role: 'assistant', content: e?.message || 'Error' }]);
    } finally {
      setInfoLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        aria-label={t('swidget_open')}
        className="fixed right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 touch-none cursor-grab active:cursor-grabbing select-none"
        style={{ bottom: `${btnBottom}px`, background: 'var(--c-accent)', color: 'var(--c-accent-ink)' }}
      >
        <MessageCircle size={26} />
      </button>
    );
  }

  const inCooldown = cooldownLeft > 0;
  const CooldownBar = () => inCooldown ? (
    <div className="flex items-center gap-2 px-3 py-2 text-xs font-medium border-t"
      style={{ borderColor: 'var(--c-border)', background: 'color-mix(in srgb, var(--c-amber) 12%, transparent)', color: 'var(--c-amber)' }}>
      <Clock size={14} className="animate-pulse" />
      <span>{cooldownNotice}</span>
    </div>
  ) : null;

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] rounded-xl flex flex-col overflow-hidden"
      style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border2)', boxShadow: 'var(--shadow-lg)' }}>

      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--c-border)' }}>
        <div className="font-semibold text-sm" style={{ color: 'var(--c-heading)' }}>{t('swidget_title')}</div>
        <button onClick={() => setOpen(false)} className="opacity-70 hover:opacity-100" aria-label="close">
          <X size={18} style={{ color: 'var(--c-text)' }} />
        </button>
      </div>

      <div className="flex border-b" style={{ borderColor: 'var(--c-border)' }}>
        {(['info', 'feedback', 'chat'] as Tab[]).map(tk => (
          <button key={tk} onClick={() => setTab(tk)}
            className="flex-1 py-2 text-xs font-medium border-b-2 transition-colors"
            style={{
              color: tab === tk ? 'var(--c-accent)' : 'var(--c-muted)',
              borderColor: tab === tk ? 'var(--c-accent)' : 'transparent',
            }}>
            {t(`swidget_tab_${tk}`)}
          </button>
        ))}
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">

        {tab === 'info' && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="text-xs px-3 py-2 rounded-lg" style={{ background: 'var(--c-panel)', color: 'var(--c-muted)' }}>
                {t('swidget_info_intro')}
              </div>
              {infoMsgs.map((m, i) => (
                <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                  <div className="max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap"
                    style={{
                      background: m.role === 'user' ? 'var(--c-accent)' : 'var(--c-panel)',
                      color: m.role === 'user' ? 'var(--c-accent-ink)' : 'var(--c-text)',
                    }}>
                    {m.content}
                  </div>
                </div>
              ))}
              {infoLoading && (
                <div className="flex justify-start items-center gap-2 text-xs" style={{ color: 'var(--c-muted)' }}>
                  <Loader2 size={14} className="animate-spin" />
                  {t('swidget_info_thinking')}
                </div>
              )}
              <div ref={infoEndRef} />
            </div>
            <CooldownBar />
            <form onSubmit={e => { e.preventDefault(); sendInfo(); }} className="p-3 border-t flex gap-2" style={{ borderColor: 'var(--c-border)' }}>
              <input
                value={infoInput}
                onChange={e => setInfoInput(e.target.value)}
                placeholder={t('swidget_info_placeholder')}
                disabled={infoLoading || inCooldown}
                className="flex-1 px-3 py-2 text-sm rounded-lg outline-none disabled:opacity-60"
                style={{ background: 'var(--c-panel)', color: 'var(--c-heading)', border: '1px solid var(--c-border2)' }}
              />
              <button type="submit" disabled={infoLoading || inCooldown || infoInput.trim().length < 2}
                className="px-3 py-2 rounded-lg disabled:opacity-50"
                style={{ background: 'var(--c-accent)', color: 'var(--c-accent-ink)' }} aria-label="send">
                <Send size={16} />
              </button>
            </form>
          </>
        )}

        {tab === 'feedback' && (
          <div className="flex-1 overflow-y-auto p-4">
            {fbDone ? (
              <div className="text-center py-8 text-sm" style={{ color: 'var(--c-text)' }}>
                ✅ {t('swidget_feedback_sent')}
              </div>
            ) : (
              <form onSubmit={e => { e.preventDefault(); submitFeedback(); }} className="space-y-3">
                <input
                  value={fbName} onChange={e => setFbName(e.target.value)}
                  placeholder={t('swidget_name')} maxLength={80}
                  className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                  style={{ background: 'var(--c-panel)', color: 'var(--c-heading)', border: '1px solid var(--c-border2)' }}
                />
                <input
                  type="email" value={fbEmail} onChange={e => setFbEmail(e.target.value)}
                  placeholder={t('swidget_email')} maxLength={200}
                  className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                  style={{ background: 'var(--c-panel)', color: 'var(--c-heading)', border: '1px solid var(--c-border2)' }}
                />
                <textarea
                  value={fbText} onChange={e => setFbText(e.target.value.slice(0, 2000))}
                  placeholder={t('swidget_message')} rows={6} maxLength={2000}
                  className="w-full px-3 py-2 text-sm rounded-lg outline-none resize-none"
                  style={{ background: 'var(--c-panel)', color: 'var(--c-heading)', border: '1px solid var(--c-border2)' }}
                />
                {inCooldown && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg"
                    style={{ background: 'color-mix(in srgb, var(--c-amber) 12%, transparent)', color: 'var(--c-amber)' }}>
                    <Clock size={14} className="animate-pulse" /><span>{cooldownNotice}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs" style={{ color: 'var(--c-muted)' }}>
                  <span>{fbText.length} / 2000</span>
                  <button type="submit" disabled={fbSending || inCooldown}
                    className="px-4 py-2 rounded-lg disabled:opacity-50"
                    style={{ background: 'var(--c-accent)', color: 'var(--c-accent-ink)' }}>
                    {fbSending ? <Loader2 size={14} className="animate-spin inline" /> : t('swidget_send')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {tab === 'chat' && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            {!user ? (
              <>
                <div className="text-sm mb-4" style={{ color: 'var(--c-text)' }}>{t('swidget_chat_login_required')}</div>
                <a href="/login" className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ background: 'var(--c-accent)', color: 'var(--c-accent-ink)' }}>
                  {t('swidget_login_btn')}
                </a>
              </>
            ) : (
              <>
                <div className="text-sm mb-4" style={{ color: 'var(--c-text)' }}>
                  💬 Direct messages with support are in your dashboard.
                </div>
                <a href="/dashboard/chat" className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ background: 'var(--c-accent)', color: 'var(--c-accent-ink)' }}>
                  Open dashboard chat
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
